import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { horseExamRequirementQueryKeys } from "api/query-keys";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { SortOrder } from "interfaces/SortOrder";
import { deepObjectUpdater } from "utils/deepObjectUpdater";
import { defineExamRequirementTabs } from "./helper";
import { ExamRequirementTab } from "./options";

interface IListHorseExamRequirementsData {
  horseExamRequirements?: racehorse360.IHorseExamRequirement[];
}

export const useExamRequirements = () => {
  const { currentUser } = useLoggedInUser();
  const queryClient = useQueryClient();
  const { useListHorseExamRequirements } = useRacehorse360Api();

  const [activeTab, setActiveTab] = useState(
    ExamRequirementTab.DEFAULT_EXAM_REQUIREMENTS
  );

  const { isExpiringSoonTab } = defineExamRequirementTabs(activeTab);

  const examRequirementRequestStatuses = useMemo(
    () =>
      isExpiringSoonTab
        ? [
            racehorse360.HorseExamRequirementStatus
              .HORSE_EXAM_REQUIREMENT_STATUS_EXAM_EXPIRING
          ]
        : [
            racehorse360.HorseExamRequirementStatus
              .HORSE_EXAM_REQUIREMENT_STATUS_EXAM_REQUIRED,
            racehorse360.HorseExamRequirementStatus
              .HORSE_EXAM_REQUIREMENT_STATUS_READY_FOR_EXAM
          ],
    [isExpiringSoonTab]
  );

  const orderByValue = useMemo(
    () =>
      isExpiringSoonTab
        ? [`reactivation_days ${SortOrder.ASC}`]
        : [`base_event_date ${SortOrder.DESC}`],
    [isExpiringSoonTab]
  );

  const request = useMemo(
    () => ({
      query: {
        trainerIds: currentUser.trainerIds,
        statuses: examRequirementRequestStatuses,
        isReactivatable: { value: isExpiringSoonTab }
      },
      getOptions: {
        select: [
          "id",
          "base_event_date",
          "status",
          "type.code",
          "type.description",
          "horse.lastRaceDate",
          "reactivation_days",
          "horse.name"
        ],
        orderBy: orderByValue
      }
    }),
    [
      currentUser.trainerIds,
      examRequirementRequestStatuses,
      isExpiringSoonTab,
      orderByValue
    ]
  );

  const { data, isLoading, compoundRequestKey } = useListHorseExamRequirements(
    horseExamRequirementQueryKeys.list(),
    request
  );

  const horseExamRequirements = data?.horseExamRequirements || [];

  const handleTabChange = useCallback((tab: ExamRequirementTab) => {
    setActiveTab(tab);
  }, []);

  const updateExamRequirement = useCallback(
    (id: string, patch: racehorse360.IHorseExamRequirement): void => {
      queryClient.setQueryData(
        compoundRequestKey,
        (
          oldData: IListHorseExamRequirementsData | undefined
        ): IListHorseExamRequirementsData | undefined => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            horseExamRequirements: (oldData.horseExamRequirements || []).map(
              item => {
                if (item.id !== id) return item;

                return new racehorse360.HorseExamRequirement(
                  deepObjectUpdater(item, patch)
                );
              }
            )
          };
        }
      );
    },
    [compoundRequestKey, queryClient]
  );

  return useMemo(
    () => ({
      activeTab,
      horseExamRequirements,
      isLoading,
      isExpiringSoonTab,
      handleTabChange,
      updateExamRequirement
    }),
    [
      activeTab,
      horseExamRequirements,
      isLoading,
      isExpiringSoonTab,
      handleTabChange,
      updateExamRequirement
    ]
  );
};
